/*----------------------------------
- GENERATED FILE
----------------------------------*/

// This file is generated by Proteum from client/pages/convert/auth/index.tsx.
// Do not edit it manually.

import type __GeneratedRouteApp from "@/client/index";

/*----------------------------------
- DEPENDANCES
----------------------------------*/

// Npm
import React from 'react';
import { AlertCircle, CheckCircle2, Chrome } from 'lucide-preact';

// Core
import Motion from '@/client/components/ui/Motion';

// App

import Button from '@/client/components/ui/Button';
import Input from '@/client/components/ui/Input';
import Page from '@/client/pages/_layout/_page';
import { Modal } from '@/client/components/ui/Modals';
import CrossPathLogo from '@/client/assets/identity/logo.svg';

/*----------------------------------
- CONTROLEUR
----------------------------------*/

export const __register = (app: __GeneratedRouteApp) => {
  const { Router } = app;
  return Router.page('/auth/:action(login|signup)/:plan?', { ...({
  auth: false,
  redirectLogged: '/',
  layout: 'convert'
}), ...{ filepath: "/app/client/pages/convert/auth/index.tsx", sourceLocation: { line: 25, column: 1 }, id: "convert_auth_index" } }, null, ({
  api,
  request,
  page,
  toast,
  Headhunters
}) => {
  page.metas['robots'] = 'noindex';
  const actionParam = request.data.action as 'login' | 'signup' | undefined;
  const planParam = request.data.plan;
  const action = actionParam ?? 'login';
  const billingPeriodParam = typeof request.data.billingPeriod === 'string' ? request.data.billingPeriod : undefined;
  const affiliateLinkParam = typeof request.data.affiliateLink === 'string' ? request.data.affiliateLink : undefined;
  const redirectPath = typeof request.data.redir === 'string' ? request.data.redir : undefined;
  const authReason = typeof request.data.reason === 'string' ? request.data.reason : undefined;
  const [currentAction, setCurrentAction] = React.useState<'login' | 'signup'>(action);
  const isSignup = currentAction === 'signup';
  const passwordToken = typeof request.data.token === 'string' ? request.data.token : undefined;
  const initialEmail = typeof request.data.email === 'string' ? request.data.email : '';
  type Step = 'auth' | 'passwordLinkSent' | 'passwordSetup';
  const initialStep: Step = passwordToken ? 'passwordSetup' : 'auth';
  const [email, setEmail] = React.useState<string>(initialEmail);
  const [password, setPassword] = React.useState<string>('');
  const [confirmPassword, setConfirmPassword] = React.useState<string>('');
  const [step, setStep] = React.useState<Step>(initialStep);
  const [error, setError] = React.useState<string>('');
  const [infoMessage, setInfoMessage] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSendingReset, setIsSendingReset] = React.useState<boolean>(false);
  const [isResetModalOpen, setIsResetModalOpen] = React.useState<boolean>(false);
  const [resetEmail, setResetEmail] = React.useState<string>(initialEmail);
  const [resetError, setResetError] = React.useState<string>('');
  const isPasswordSetup = step === 'passwordSetup';
  const termsUrl = 'https://www.becrosspath.com/terms/marketplace';
  React.useEffect(() => {
    if (passwordToken || email) return;
    if (typeof window === 'undefined') return;
    const memorisedEmail = localStorage?.getItem('auth.email');
    if (memorisedEmail && memorisedEmail !== 'undefined') setEmail(memorisedEmail);
  }, [email, passwordToken]);
  const handleSwitchTab = React.useCallback((target: 'login' | 'signup') => {
    if (target === currentAction) return;
    setCurrentAction(target);
    setStep(passwordToken ? 'passwordSetup' : 'auth');
    setError('');
    setInfoMessage('');
    setPassword('');
    setConfirmPassword('');
    if (typeof window !== 'undefined') {
      const planSuffix = planParam ? `/${planParam}` : '';
      const query = new URLSearchParams();
      if (redirectPath) query.set('redir', redirectPath);
      if (authReason) query.set('reason', authReason);
      if (billingPeriodParam) query.set('billingPeriod', billingPeriodParam);
      if (affiliateLinkParam) query.set('affiliateLink', affiliateLinkParam);
      const queryString = query.toString();
      const newUrl = `/auth/${target}${planSuffix}${queryString ? `?${queryString}` : ''}`;
      window.history.replaceState(null, '', newUrl);
    }
  }, [affiliateLinkParam, authReason, billingPeriodParam, currentAction, passwordToken, planParam, redirectPath]);
  const getErrorMessage = (err: unknown): string => {
    if (!err) return 'An unexpected error occurred.';
    if (typeof err === 'string') return err;
    if (err instanceof Error) return err.message;
    if (typeof (err as {
      message?: string;
    }).message === 'string') return (err as {
      message: string;
    }).message;
    return 'An unexpected error occurred.';
  };
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setInfoMessage('');
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    const trimmedEmail = email.trim();
    const handleAsyncError = (err: unknown) => {
      console.error('handleSubmit error', err);
      setIsLoading(false);
      const message = getErrorMessage(err);
      if (message.toLowerCase().includes('password') && message.toLowerCase().includes('link')) {
        setInfoMessage(message);
        setStep('passwordLinkSent');
      } else {
        setError(message);
      }
    };
    if (isPasswordSetup && passwordToken) {
      if (password.length < 8) {
        setError('Your password must be at least 8 characters long.');
        return;
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match.');
        return;
      }
      setIsLoading(true);
      Headhunters.completePasswordSetup({
        token: passwordToken,
        password
      }).then(response => {
        localStorage?.setItem('auth.email', trimmedEmail);
        window.location.href = response?.redirect ?? '/';
      }).catch(handleAsyncError);
      return;
    }
    if (isSignup) {
      setIsLoading(true);
      Headhunters.signup({
        email: trimmedEmail,
        packId: planParam || undefined,
        billingPeriod: billingPeriodParam,
        affiliateLinkId: affiliateLinkParam,
        redirectPath
      }).then(response => {
        localStorage?.setItem('auth.email', trimmedEmail);
        window.location.href = response?.redirect ?? '/';
      }).catch(handleAsyncError);
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }
    setIsLoading(true);
    Headhunters.login({
      email: trimmedEmail,
      password,
      packId: planParam || undefined,
      billingPeriod: billingPeriodParam,
      affiliateLinkId: affiliateLinkParam,
      redirectPath
    }).then(response => {
      console.log('handleSubmit response', response);
      localStorage?.setItem('auth.email', trimmedEmail);
      window.location.href = response?.redirect ?? '/';
    }).catch(handleAsyncError).finally(() => {
      setIsLoading(false);
    });
  };
  const sendPasswordLink = React.useCallback((targetEmail: string) => {
    const trimmedEmail = targetEmail?.trim?.() ?? '';
    if (!trimmedEmail) {
      toast.error('Please enter your email address');
      setResetError('Please enter a valid email address to receive the link.');
      return;
    }
    setError('');
    setResetError('');
    setInfoMessage('');
    setIsSendingReset(true);
    Headhunters.sendPasswordSetupLink({
      email: trimmedEmail
    }).then(() => {
      setInfoMessage('We just sent you an email to set your password. Check your inbox and follow the instructions.');
      setStep('passwordLinkSent');
      setIsResetModalOpen(false);
      setEmail(trimmedEmail);
      setResetEmail(trimmedEmail);
      if (typeof window !== 'undefined') localStorage?.setItem('auth.email', trimmedEmail);
    }).catch(err => {
      const message = getErrorMessage(err);
      setResetError(message);
    }).finally(() => {
      setIsSendingReset(false);
    });
  }, [getErrorMessage, toast]);
  const handleGoogleAuth = React.useCallback(() => {
    const query = new URLSearchParams();
    if (planParam) query.set('packId', planParam);
    if (billingPeriodParam) query.set('billingPeriod', billingPeriodParam);
    if (affiliateLinkParam) query.set('affiliateLink', affiliateLinkParam);
    if (redirectPath) query.set('redir', redirectPath);
    if (authReason) query.set('reason', authReason);
    const queryString = query.toString();
    window.location.href = `/auth/google${queryString ? `?${queryString}` : ''}`;
  }, [affiliateLinkParam, authReason, billingPeriodParam, planParam, redirectPath]);
  const headerTitle = isPasswordSetup ? 'Create a new password' : isSignup ? 'Join CrossPath' : 'Welcome Back';
  const headerSubtitle = isPasswordSetup ? 'Choose a new password to secure your account.' : isSignup ? 'Enter your email to start using CrossPath.' : 'Sign in to your account';
  const submitLabel = isPasswordSetup ? isLoading ? 'Saving password...' : 'Save password & continue' : isSignup ? isLoading ? 'Creating your account...' : 'Create account' : isLoading ? 'Signing you in...' : 'Log in';
  const passwordLabel = isPasswordSetup ? 'New password' : 'Password';
  const confirmLabel = isPasswordSetup ? 'Confirm new password' : 'Confirm password';
  const showPassword = isPasswordSetup || !isSignup;
  const showConfirmPassword = isPasswordSetup;
  const showOAuth = !isPasswordSetup;
  const showTroublesLink = !isSignup && !isPasswordSetup;
  const defaultStepAfterInfo: Step = passwordToken ? 'passwordSetup' : 'auth';
  const activeTab = currentAction;
  const renderTabs = <div className="flex w-full rounded-full bg-gray-100 p-1">
            {(['login', 'signup'] as const).map(tab => {
      const isActive = tab === activeTab;
      return <button key={tab} type="button" data-testid={tab === 'login' ? 'auth-tab-login' : 'auth-tab-signup'} onClick={() => handleSwitchTab(tab)} disabled={isActive} className={['flex-1 rounded-full px-4 py-2 text-sm font-semibold transition', isActive ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900 focus:text-gray-900'].join(' ')}>
                        {tab === 'login' ? 'Log in' : 'Sign up'}
                    </button>;
    })}
        </div>;
  const resetModal = <Modal isOpen={isResetModalOpen} onClose={() => {
    setIsResetModalOpen(false);
    setResetError('');
  }} title="Send a password link" size="md" footer={<div className="flex justify-end gap-3">
                    <Button type="button" variant="ghost" onClick={() => {
      setIsResetModalOpen(false);
      setResetError('');
    }}>
                        Cancel
                    </Button>
                    <Button type="button" disabled={isSendingReset} onClick={() => sendPasswordLink(resetEmail)} className="bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white px-6">
                        {isSendingReset ? 'Sending…' : 'Send link'}
                    </Button>
                </div>}>
            <div className="space-y-5">
                <p className="text-sm text-gray-600">
                    We’ll send you a secure link to create or reset your password. Open it from this device to finish
                    signing in.
                </p>

                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
                    <Input type="email" value={resetEmail} onChange={event => setResetEmail(event.currentTarget.value)} placeholder="name@company.com" autoComplete="off" className="w-full" required />
                </div>

                {resetError && <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                        <AlertCircle size={16} className="mt-0.5" />
                        <span>{resetError}</span>
                    </div>}

                <p className="text-xs text-gray-500">
                    This link expires after 12 hours and can only be used once. If you still can’t sign in, reply to the
                    email and we’ll help you personally.
                </p>
            </div>
        </Modal>;
  const authContent = <>
            <div className="text-center mb-8">
                <div className="flex items-center justify-center gap-3 mb-4">
                    <img src={CrossPathLogo} alt="CrossPath Logo" className="w-12 h-12 object-contain" />
                    <span className="text-3xl font-bold bg-gradient-to-r from-orange-600 to-pink-600 bg-clip-text text-transparent">
                        CrossPath
                    </span>
                </div>
                <h1 className="text-2xl font-semibold text-gray-900 mb-2">{headerTitle}</h1>
                <p className="text-gray-600">{headerSubtitle}</p>
            </div>

            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
                <div className="mb-6">{renderTabs}</div>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email address</label>
                        <Input type="email" value={email} onChange={event => setEmail(event.currentTarget.value)} placeholder="Enter your email" required className="w-full" autoComplete="off" readOnly={isPasswordSetup} data-testid="auth-email-input" />
                    </div>

                    {showPassword ? <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">{passwordLabel}</label>
                        <Input type="password" value={password} onChange={event => setPassword(event.currentTarget.value)} placeholder={isPasswordSetup ? 'Create a new password' : 'Enter your password'} required minLength={8} className="w-full" autoComplete="off"
          /* Users are getting "bad pasword" issues */ data-testid="auth-password-input" />
                    </div> : null}

                    {showConfirmPassword ? <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">{confirmLabel}</label>
                            <Input type="password" value={confirmPassword} onChange={event => setConfirmPassword(event.currentTarget.value)} placeholder="Re-enter your password" required minLength={8} className="w-full" autoComplete="new-password" data-testid="auth-confirm-password-input" />
                        </div> : null}

                    <Button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600 text-white py-3 font-semibold" data-testid="auth-submit-button">
                        {submitLabel}
                    </Button>

                    {showTroublesLink ? <button type="button" onClick={() => {
          setResetEmail(email);
          setResetError('');
          setIsResetModalOpen(true);
        }} disabled={isSendingReset} className="text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors">
                            {isSendingReset ? 'Sending link…' : 'Forgot password?'}
                        </button> : null}

                    {showOAuth ? <>
                            <div className="flex items-center gap-3 text-xs uppercase text-gray-400">
                                <span className="h-px flex-1 bg-gray-200" />
                                <span>Or</span>
                                <span className="h-px flex-1 bg-gray-200" />
                            </div>

                            <Button type="button" variant="outline" onClick={handleGoogleAuth} className="w-full gap-3 bg-white border-gray-200 text-gray-700 hover:bg-gray-50 hover:text-gray-900" aria-label={isSignup ? 'Sign up with Google' : 'Continue with Google'}>
                                <Chrome size={18} />
                                {isSignup ? 'Sign up with Google' : 'Continue with Google'}
                            </Button>
                        </> : null}

                    {isSignup && !isPasswordSetup ? <p className="text-xs text-gray-500 leading-5">
                            By signing up, you accept our{' '}
                            <a href={termsUrl} target="_blank" rel="noopener noreferrer" className="font-medium text-orange-600 hover:text-orange-700 underline underline-offset-2">
                                terms and conditions of usage
                            </a>
                            .
                        </p> : null}

                    {error && <Motion tag="div" initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="auth-error">
                            <AlertCircle size={16} />
                            {error}
                        </Motion>}
                </form>
            </div>
        </>;
  const passwordLinkSentContent = <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 space-y-6 text-center">
            <div className="mb-6">{renderTabs}</div>
            <CheckCircle2 className="mx-auto h-14 w-14 text-green-500" />
            <div className="space-y-2">
                <h2 className="text-2xl font-semibold text-gray-900">Check your mailbox!</h2>
                <p className="text-gray-600 text-sm leading-6">
                    {infoMessage || <>
                            We just sent a password setup link to{' '}
                            <span className="font-semibold text-gray-900">{email || 'your email address'}</span>. Follow
                            the instructions inside to create your password.
                        </>}
                </p>
            </div>
            <div className="rounded-xl bg-orange-50 border border-orange-200 px-4 py-3 text-orange-700 text-sm">
                If you don’t see the email after a few minutes, check your spam folder or make sure you entered the
                correct address.
            </div>
            <div className="space-y-3">
                <Button type="button" onClick={() => sendPasswordLink(email)} disabled={isSendingReset} className="w-full">
                    {isSendingReset ? 'Resending…' : 'Resend password link'}
                </Button>
                <Button type="button" variant="ghost" onClick={() => {
        setStep(defaultStepAfterInfo);
        setError('');
        setInfoMessage('');
        setPassword('');
        setConfirmPassword('');
      }} className="w-full">
                    Use a different email
                </Button>
            </div>
        </div>;
  const content = step === 'passwordLinkSent' ? passwordLinkSentContent : authContent;
  const pageContent = <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-pink-50 flex items-center justify-center p-4">
            {resetModal}
            <Motion tag="div" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="w-full max-w-md">
                {content}
            </Motion>
        </div>;
  return pageContent;
});
};
